import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import EventKPIs from './components/EventKPIs';
import EventVolumeChart from './components/EventVolumeChart';
import ConversionFunnel from './components/ConversionFunnel';
import TopFormsTable from './components/TopFormsTable';
import FieldDropOff from './components/FieldDropOff';
import LiveEventFeed from './components/LiveEventFeed';
import BehaviorBreakdown from './components/BehaviorBreakdown';
import {
  getEventKPIs,
  getRecentEvents,
} from '../../services/eventAnalyticsService';

const TIME_RANGES = [
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: '90D', value: '90d' },
  { label: '1Y', value: '1y' },
];

export const EventAnalyticsContent = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [timeRange, setTimeRange] = useState('30d');
  const [kpis, setKpis] = useState(null);
  const [events, setEvents] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/authentication');
      return;
    }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = () => {
    setKpis(getEventKPIs());
    setEvents(getRecentEvents());
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      loadData();
      setLastRefreshed(new Date());
      setRefreshing(false);
    }, 800);
  };

  if (authLoading || !kpis) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Icon name="Loader2" size={20} className="animate-spin" />
          <span className="text-sm">Loading analytics…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Zap" size={16} color="var(--color-primary)" />
                </div>
                <h1 className="text-xl font-bold text-foreground">Event Analytics</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                Form performance, user behavior &amp; conversion funnels
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Time range selector */}
              <div className="flex items-center bg-muted rounded-lg p-1 gap-0.5">
                {TIME_RANGES?.map((tr) => (
                  <button
                    key={tr?.value}
                    onClick={() => setTimeRange(tr?.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                      timeRange === tr?.value
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tr?.label}
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2"
              >
                <Icon
                  name="RefreshCw"
                  size={13}
                  className={refreshing ? 'animate-spin' : ''}
                />
                <span className="text-xs">Refresh</span>
              </Button>
            </div>
          </div>

          {/* Last refreshed */}
          <p className="text-[11px] text-muted-foreground mb-5">
            Last updated: {lastRefreshed?.toLocaleTimeString()}
          </p>

          {/* KPI Row */}
          <div className="mb-6">
            <EventKPIs data={kpis} />
          </div>

          {/* Main grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            {/* Event volume chart — spans 2 cols */}
            <div className="xl:col-span-2">
              <EventVolumeChart />
            </div>
            {/* Live feed */}
            <div className="xl:col-span-1">
              <LiveEventFeed events={events} />
            </div>
          </div>

          {/* Second row */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            {/* Conversion funnel */}
            <div className="xl:col-span-1">
              <ConversionFunnel />
            </div>
            {/* Field drop-off */}
            <div className="xl:col-span-1">
              <FieldDropOff />
            </div>
            {/* Behavior breakdown */}
            <div className="xl:col-span-1">
              <BehaviorBreakdown />
            </div>
          </div>

          {/* Top forms table — full width */}
          <TopFormsTable />
    </div>
  );
};

const EventAnalyticsDashboard = () => {
  return (
    <NavigationBar>
      <EventAnalyticsContent />
    </NavigationBar>
  );
};

export default EventAnalyticsDashboard;
